import React, { Component } from 'react';
import { Modal, ModalHeader, ModalBody, Row, Col, Input, ModalFooter, Button, Label } from 'reactstrap';
import CopyString from '../CopyString/CopyString';
import ConfirmModal from '../Modal/ConfirmModal';
import CompanyContext from '../../auth/CompanyContext';
import { Prompt } from 'react-router-dom';

const getUniqueContacts = (contacts) => {
    if (!Array.isArray(contacts)) return [];

    const seenContactGUIDs = new Set();
    return contacts.filter((contact) => {
        const contactGUID = String(contact?.linkedID || '').trim().toLowerCase();
        if (!contactGUID) return true;
        if (seenContactGUIDs.has(contactGUID)) return false;

        seenContactGUIDs.add(contactGUID);
        return true;
    });
};

const parseContactsResponse = async (response) => {
    if (!response.ok) throw new Error(`Unable to load contacts (${response.status})`);

    const text = await response.text();
    if (!text.trim()) return [];

    try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) return parsed;
        if (Array.isArray(parsed?.data)) return parsed.data;
        if (parsed && !parsed.error) return [parsed];
    } catch (error) {
        return text
            .split('\n')
            .map(line => line.trim())
            .filter(Boolean)
            .map(line => {
                try { return JSON.parse(line); }
                catch (parseError) { return null; }
            })
            .filter(contact => contact && !contact.error);
    }

    return [];
};

const mergeTemplatesByID = (templates, existingTemplates = []) => {
    const byID = new Map();
    [...templates, ...existingTemplates].forEach(template => {
        if (template?.ID != null && !byID.has(String(template.ID))) {
            byID.set(String(template.ID), template);
        }
    });
    return Array.from(byID.values());
};

class Marketing2ActionsModal extends Component {
    static contextType = CompanyContext;

    constructor(props) {
        super(props);

        this.state = {
            isOpen: false,
            contacts: [],
            obj: {},
            originalState: JSON.stringify({}),
            users: [],
            emails: [],
            calls: [],
            invitations: [],
            retHTML: '',
            contentNotesUpdated: false,
            review: false,
            confirmModalOpen: false
        };

        if (this.props.setOpenActionsModal) {
            this.props.setOpenActionsModal((obj, retHTML, contacts, review) => {
                this.setState({
                    isOpen: true,
                    obj: obj,
                    originalState: JSON.stringify(obj),
                    retHTML: retHTML,
                    contacts: getUniqueContacts(contacts),
                    review: review != null ? review : false
                }, () => {
                    if (!contacts && this.state.obj.secID != null && this.state.obj.secID !== '') {
                        this.getContacts();
                    }
                    this.getReferencedTemplate(obj.componentCategory, obj.componentCategoryItemID);
                })
            })
        }
        if (this.props.setCloseActionsModal) {
            this.props.setCloseActionsModal(this.hideModal)
        }
    }

    componentDidMount = () => {
        this.getUsers();
        this.getMarketingEmails();
        this.getMarketingCalls();
        this.getMarketingInvitations();
        window.addEventListener('beforeunload', this.handleBeforeUnload);
    }

    componentWillUnmount = () => {
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
    }

    handleBeforeUnload = (e) => {
        console.log('test')
        // if (this.state.isDirty) {
        e.preventDefault();
        e.returnValue = ''; // Standard for modern browsers
        // }
    };

    handleEmailClick = () => {
        window.removeEventListener("beforeunload", this.handleBeforeUnload);
        setTimeout(() => {
            window.addEventListener("beforeunload", this.handleBeforeUnload);
        }, 2500);
    }

    getUsers = () => {
        // console.log('Marketing2ActionsModal.js/getUsers()');
        fetch('/api/users/', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + this.props.clientToken,
                'X-Database': this.context.company
            }
        })
            .then(results => results.json())
            .then(results => {
                this.setState({
                    users: results.data
                });
            });
    }

    getContacts = () => {
        const secID = this.state.obj.secID;
        fetch(`/api/contacts/${this.props.user}/${secID}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/x-ndjson',
                'Authorization': 'Bearer ' + this.props.clientToken,
                'X-Database': this.context.company
            }
        })
            .then(parseContactsResponse)
            .then(contacts => {
                if (this.state.obj.secID !== secID) return;
                this.setState({
                    contacts: getUniqueContacts(contacts),
                });
            })
            .catch(() => {
                if (this.state.obj.secID === secID) this.setState({ contacts: [] });
            });
        return;
    }

    getMarketingEmails = () => {
        fetch('/api/marketing2Emails/', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + this.props.clientToken,
                'X-Database': this.context.company
            }
        })
            .then(results => results.json())
            .then(results => {
                this.setState(prevState => ({
                    emails: mergeTemplatesByID(results.data || [], prevState.emails)
                }));
            });
    }

    getReferencedTemplate = (category, templateID) => {
        const categoryKey = `${String(category || '').toLowerCase()}s`;
        const endpointByCategory = {
            emails: 'marketing2Emails',
            calls: 'marketing2Calls',
            invitations: 'marketing2Invitations'
        };
        const parsedTemplateID = parseInt(templateID);
        if (!endpointByCategory[categoryKey] || !Number.isInteger(parsedTemplateID)) return;

        fetch(`/api/${endpointByCategory[categoryKey]}/${parsedTemplateID}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + this.props.clientToken,
                'X-Database': this.context.company
            }
        })
            .then(results => results.json())
            .then(results => {
                this.setState(prevState => ({
                    [categoryKey]: mergeTemplatesByID(results.data || [], prevState[categoryKey])
                }));
            });
    }

    getMarketingCalls = () => {
        fetch('/api/marketing2Calls/', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + this.props.clientToken,
                'X-Database': this.context.company
            }
        })
            .then(results => results.json())
            .then(results => {
                this.setState(prevState => ({
                    calls: mergeTemplatesByID(results.data || [], prevState.calls)
                }));
            });
    }

    getMarketingInvitations = () => {
        fetch('/api/marketing2Invitations/', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + this.props.clientToken,
                'X-Database': this.context.company
            }
        })
            .then(results => results.json())
            .then(results => {
                this.setState(prevState => ({
                    invitations: mergeTemplatesByID(results.data || [], prevState.invitations)
                }));
            });
    }

    showModal = (isOpen) => {
        this.setState({ isOpen: isOpen });
    }

    hideModal = () => {
        this.setState({
            isOpen: false,
            obj: {},
            originalState: JSON.stringify({}),
            retHTML: '',
            contentNotesUpdated: false,
            contacts: [],
            review: false,
            confirmModalOpen: false
        })
    }

    onChangeMarketingActivity = (e, cb) => {
        if (e.persist != null) e.persist();
        let contentNotesUpdated = false;
        this.setState((prevState) => {
            let thisMarketingActivity = {};
            let activityObj = prevState.obj.content != null && prevState.obj.content !== '' ? prevState.obj.content : {};
            switch (e.target.name) {
                case "subject":
                case "message":
                case "sent":
                    activityObj[e.target.name] = e.target.value;
                    thisMarketingActivity.content = activityObj;
                    contentNotesUpdated = true;
                    break;
                case "to":
                    if (activityObj.to == null) activityObj.to = [];
                    if (!e.target.checked) {
                        const removeIndex = activityObj.to.findIndex(item => item.linkedID === e.target.id.split('_')[1]);
                        activityObj.to.splice(removeIndex, 1);
                    } else {
                        activityObj.to.push({
                            linkedID: e.target.id.split('_')[1],
                            name: e.target.id.split('|')[0].split('_').splice(2).join("_"),
                            email: e.target.id.split('|')[1]
                        });
                    }
                    thisMarketingActivity.content = activityObj;
                    break;
                case "attachment":
                    if (activityObj.attachments == null) activityObj.attachments = [];
                    if (!e.target.checked) {
                        const removeIndex = activityObj.attachments.findIndex(attachment => attachment.FileID === e.target.id.split('_')[1]);
                        activityObj.attachments.splice(removeIndex, 1);
                    } else {
                        activityObj.attachments.push({ FileID: e.target.id.split('_')[1], FileName: e.target.id.split('_').splice(2).join("_") });
                    }
                    thisMarketingActivity.content = activityObj;
                    break;
                case "notes":
                    thisMarketingActivity[e.target.name] = e.target.value;
                    contentNotesUpdated = true;
                    break;
                default:
                    thisMarketingActivity[e.target.name] = e.target.value;
            }
            return { obj: { ...prevState.obj, ...thisMarketingActivity }, contentNotesUpdated: contentNotesUpdated };
        }, () => {
            if (cb != null) cb();
        });
    }

    getUser = (auth0) => {
        const index = this.state.users.findIndex(user => user.Auth0 === auth0);
        let blankObj = { Active: false, Auth0: false };
        blankObj['Email Address'] = '';
        blankObj['First Name'] = 'N/A';
        blankObj['Last Name'] = '';
        return (index > -1) ? this.state.users[index] : blankObj
    }

    getContact = (linkedID) => {
        // console.log('MarketingDemographicsDashboardTile.js/getContact (linkedID)',linkedID,this.state.contacts)
        const index = this.state.contacts != null && this.state.contacts.length > 0 ? this.state.contacts.findIndex(contact => contact.linkedID === linkedID) : -1;
        // console.log('index',index)
        if (index > -1) {
            // const emailAddressIndex = this.state.contacts[index].children.findIndex(child => child.type === 'Email Address')
            // const nameAddressIndex = this.state.contacts[index].children.findIndex(child => child.type === 'Contact Name')
            return {
                emailAddress: this.state.contacts[index].children['Email Address'],
                name: this.state.contacts[index].children['Contact Name']
            }
        } else {
            return { emailAddress: '', firstName: 'N/A', lastName: '' }
        }
    }

    formatDate = (d) => {
        if (parseInt(String(d).substring(0, 4)) <= 2000 || d == null || d === '') return "";
        return parseInt(String(d).substring(5, 7)) + '/' + parseInt(String(d).substring(8, 10)) + '/' + parseInt(String(d).substring(0, 4));
    }

    getHeader = (selectedCategory, selectedItem) => {
        switch (selectedCategory) {
            case "emails":
                return /*"Subject: " + */this.state[selectedCategory].filter(item => item.ID === parseInt(selectedItem))[0]?.Subject || '';
            case "calls":
                return "";
            case "invitations":
                return this.formatDate(this.state[selectedCategory].filter(item => item.ID === parseInt(selectedItem))[0]?.Date);
            default:
                return "";
        }
    }

    getType = (selectedCategory, selectedItem) => {
        switch (selectedCategory) {
            case "emails":
                return this.state[selectedCategory].filter(item => item.ID === parseInt(selectedItem))[0]?.Text || '';
            case "calls":
                return this.state[selectedCategory].filter(item => item.ID === parseInt(selectedItem))[0]?.Script || '';
            case "invitations":
                return this.state[selectedCategory].filter(item => item.ID === parseInt(selectedItem))[0]?.Description || '';
            default:
                return "";
        }
    }

    getAttachments = (selectedCategory, selectedItem) => {
        switch (selectedCategory) {
            case "emails":
                if (this.state[selectedCategory].filter(item => item.ID === parseInt(selectedItem)).length === 0 ||
                    this.state[selectedCategory].filter(item => item.ID === parseInt(selectedItem))[0].Attachments == null) return [];
                return JSON.parse(this.state[selectedCategory].filter(item => item.ID === parseInt(selectedItem))[0].Attachments);
            case "invitations":
                if (this.state[selectedCategory].filter(item => item.ID === parseInt(selectedItem)).length === 0 ||
                    this.state[selectedCategory].filter(item => item.ID === parseInt(selectedItem))[0].Attachments == null) return [];
                return JSON.parse(this.state[selectedCategory].filter(item => item.ID === parseInt(selectedItem))[0].Attachments);
            default:
                return [];
        }
    }

    handleContentNotes = () => {
        fetch('/api/marketing2ComponentsCompanies/contentNotes/' + this.state.obj.componentCompanyID, {
            method: 'PUT',
            body: JSON.stringify({
                componentCompanyID: this.state.obj.componentCompanyID,
                content: this.state.obj.content,
                notes: this.state.obj.notes
            }),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + this.props.clientToken,
                'X-Database': this.context.company
            }
        })
            .then(results => results.json())
            .then(results => {
                if (results.success) {
                    this.props.reload();
                    this.hideModal();
                }
            });
    }

    mergeFields = (selectedCategory, str) => {
        // let contact = this.state.contacts.filter(contact => contact.linkedID === this.state[selectedCategory].contactLinkedID).length > 0 ? this.state.contacts.filter(contact => contact.linkedID === this.state[selectedCategory].contactLinkedID)[0].children.filter(child => child.type === 'Contact Name') : [];
        let from = this.getUser(this.state[selectedCategory].auth0);
        let to = this.getContact(this.state[selectedCategory].contactLinkedIDs);
        // let to = this.state.contacts.filter(contact => contact.linkedID === this.state[selectedCategory].contactLinkedID).length > 0 ? this.state.contacts.filter(contact => contact.linkedID === this.state[selectedCategory].contactLinkedID)[0].children.filter(child => child.type === 'Email Address') : [];
        // console.log('x', selectedCategory, str)
        if (!str) return "";
        let retStr = str.trim().replace(/\${(.*?)}/gi, (item) => {
            switch (item.substring(2, item.length - 1)) {
                case 'Sender First Name':
                    return from['First Name'];
                case 'Sender Last Name':
                    return from['Last Name'];
                case 'Sender Contact Info':
                    return from['Email Address'];
                case 'Recipient Email Address':
                    return to.emailAddress;
                case 'Recipient Name':
                    // return (contact.length === 1) ? [...contact[0].comment.split(' ')].shift() : item.substring(2, item.length - 1);
                    return to.name;
                case 'Current Date':
                    return new Date().toLocaleDateString('en-US', {
                        // weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    });
                default:
                    return item.substring(2, item.length - 1);
            }
        })
        return retStr;
    }

    sendEmail = (e) => {
        const from = this.getUser(this.state.obj.auth0);
        fetch('/api/mail/', {
            method: 'POST',
            body: JSON.stringify({
                from: `${from['First Name']} ${from['Last Name']} <${from['Email Address']}>`,
                // to: this.mergeFields('obj', ['${Recipient ', 'First Name}'].join('')) + ' ' + this.mergeFields('obj', ['${Recipient ', 'Last Name}'].join('')) + ' <' + this.mergeFields('obj', ['${Recipient ', 'Email Address}'].join('')) + '>',
                to: this.state.obj.content.to != null && this.state.obj.content.to.length > 0 ? this.state.obj.content.to.map(name => this.getContact(name.linkedID)).map(name => `${name.name} <${name.emailAddress}>`).join(', ') : '',
                subject: this.state.obj.content.subject !== '' && this.state.obj.content.subject != null ? this.state.obj.content.subject : this.mergeFields('obj', this.getHeader(String(this.state.obj.componentCategory).toLowerCase() + 's', this.state.obj.componentCategoryItemID)),
                message: this.state.obj.content.message !== '' && this.state.obj.content.message != null ? this.state.obj.content.message : this.mergeFields('obj', this.getType(String(this.state.obj.componentCategory).toLowerCase() + 's', this.state.obj.componentCategoryItemID)),
                attachments: this.state.obj.content.attachments
            }),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + this.props.clientToken,
                'X-Database': this.context.company
            }
        })
            // .then(results => results.json())
            .then(results => {
                if (results.status === 200) {
                    this.onChangeMarketingActivity({ target: { name: 'sent', value: new Date().getTime() }, persist: () => null }, () => {
                        this.handleContentNotes();
                        this.props.markCompanyComponentComplete(this.state.obj.campaignCompanyID, this.state.obj.componentCompanyID, this.state.obj.componentID, this.state.obj.campaignID, this.state.obj.secID, this.state.obj.componentIsActivity ? this.state.obj.content : null, this.state.obj.auth0, this.state.obj.linkedCampaignCompanyIDs)
                    })
                }
            });
    }

    reset = () => {
        this.setState((prevState) => {
            let activityObj = prevState.obj;
            activityObj.content = {}
            activityObj.notes = '';
            return { obj: activityObj, originalState: JSON.stringify(activityObj), contentNotesUpdated: false };
        })
    }

    render() {
        // let name = '';
        const activityObj = (this.state.obj != null && this.state.obj.content != null && this.state.obj.content !== '' && this.state.obj.componentIsActivity) ? this.state.obj.content : {};
        // let to = activityObj.to != null ? activityObj.to : '';
        let subject = activityObj.subject != null ? activityObj.subject : '';
        let message = activityObj.message != null ? activityObj.message : '';
        let attachments = this.getAttachments(String(this.state.obj.componentCategory).toLowerCase() + 's', this.state.obj.componentCategoryItemID);
        if (this.state.obj != null &&
            this.state.obj.componentCompanyID > 0 &&
            this.state.obj.componentCategory !== '' &&
            this.state[String(this.state.obj.componentCategory).toLowerCase() + 's'] != null &&
            this.state.obj.componentCategoryItemID > 0) {
            // name = this.state[String(this.state.obj.componentCategory).toLowerCase() + 's']
            //     .filter(item => parseInt(item.ID) === parseInt(this.state.obj.componentCategoryItemID))[0].Name;
            if (subject === '') subject = this.mergeFields('obj', this.getHeader(String(this.state.obj.componentCategory).toLowerCase() + 's', this.state.obj.componentCategoryItemID));
            if (message === '') message = this.mergeFields('obj', this.getType(String(this.state.obj.componentCategory).toLowerCase() + 's', this.state.obj.componentCategoryItemID));
            // if (to === '') to = this.mergeFields('obj', ['${Recipient ', 'First Name}'].join('')) + ' ' + this.mergeFields('obj', ['${Recipient ', 'Last Name}'].join('')) + ' <' + this.mergeFields('obj', ['${Recipient ', 'Email Address}'].join('')) + '>'
            // if (attachments.length === 0) 
        }
        // const toValue = (this.state.obj.to !== '' && this.state.obj.to != null ? this.state.obj.to : to);
        // console.log('obj', this.state.obj)
        // console.log('obj', String(this.state.obj.componentCategory).toLowerCase() + 's', this.state[String(this.state.obj.componentCategory).toLowerCase() + 's'])
        // // console.log('name', name)
        // console.log('subject', subject, subject != null && subject !== '' && new Date(subject).toISOString().substring(0,10))
        // console.log('message', message)
        // console.log('to', to)
        // console.log('toValue', toValue)
        // console.log('atta',attachments)

        return (
            <Modal size='xl' centered isOpen={this.state.isOpen} onClosed={this.hideModal}>
                <Prompt
                    when={this.state.originalState !== JSON.stringify(this.state.obj)}
                    message="You have unsaved changes, are you sure you want to leave?"
                />
                <ModalHeader toggle={this.hideModal}>
                    Actions
                </ModalHeader>
                <ModalBody>

                    {this.state.obj.componentIsActivity && (this.state.obj.componentCategory === 'Email' || this.state.obj.componentCategory === 'Invitation') &&
                        <>
                            <Row className='mt-2'>
                                <Col md={3} className='text-right font-weight-bold pt-2'>
                                    From:
                                </Col>
                                <Col md={9}>
                                    <Input
                                        type="select"
                                        name="auth0"
                                        disabled={this.state.obj.sentTimestamp != null || this.state.review}
                                        onChange={(e) => this.onChangeMarketingActivity(e)}
                                        value={this.state.obj.auth0}
                                    >
                                        <option value=''>Select Sender</option>
                                        {this.state.users.filter(user => user.Active).map(user => (
                                            <option key={user.Auth0} value={user.Auth0}>{user['Full Name']} &lt;{user['Email Address']}&gt;</option>
                                        ))}
                                    </Input>
                                </Col>
                            </Row>
                            <Row className='mt-2'>
                                <Col md={3} className='text-right font-weight-bold pt-2'>
                                    To:
                                </Col>
                                <Col md={9} className='pl-5'>
                                    {this.state.contacts != null && this.state.contacts.length > 0 && this.state.contacts.filter(contact => contact.active).map(contact => (
                                        <Label key={contact.linkedID} className='my-2'>
                                            <Input
                                                type="checkbox"
                                                disabled={this.state.obj.sentTimestamp != null || this.state.review || !contact.children['Email Address']}
                                                name="to"
                                                id={"to_" + contact.linkedID + '_' + contact.children['Contact Name'] + '|' + contact.children['Email Address']}
                                                onChange={(e) => this.onChangeMarketingActivity(e)}
                                                checked={this.state.obj.content != null && this.state.obj.content.to != null ? this.state.obj.content.to.findIndex(a2 => a2.linkedID === contact.linkedID) > -1 : false}
                                            >
                                            </Input>
                                            {contact.children['Contact Name']} &lt;{contact.children['Email Address']}&gt; {contact.children.Title != null && contact.children.Title !== '' ? ` (${contact.children.Title})` : ''}&nbsp;<CopyString textToCopy={contact.children['Email Address']} />
                                        </Label>
                                    ))}
                                    {/* <Input
                                        type="select"
                                        name="to"
                                        disabled={this.state.obj.sentTimestamp != null || this.state.review}
                                        onChange={(e) => this.onChangeMarketingActivity(e)}
                                        value={to}
                                    >
                                        <option value=''>Contact</option>
                                        {this.state.contacts != null && this.state.contacts.length > 0 && this.state.contacts.map(contact => (
                                            <option key={contact.linkedID} value={contact.linkedID}>{contact.children['Contact Name']} &lt;{contact.children['Email Address']}&gt; {contact.children.Title != null && contact.children.Title !== '' ? `(${contact.children.Title})` : ''}</option>
                                        ))}
                                    </Input> */}
                                </Col>
                            </Row>
                            {/* <Row className='mt-2'>
                        <Col md={3} className='text-right font-weight-bold pt-2'>
                            To (other):
                        </Col>
                        <Col md={9}>
                            <Input
                                name="to"
                                disabled={this.state.obj.sentTimestamp != null || (this.state.obj.toContact != null && this.state.obj.toContact !== '')}
                                className={!(/^[^\s@]+@[^\s@]+\.[^\s@]+$/).test(toValue.split(' ').pop()) ? "text-danger" : ""}
                                onChange={(e) => this.onChangeMarketingActivity(e)}
                                value={toValue}
                            >
                            </Input>
                        </Col>
                    </Row> */}
                            <Row className='mt-2'>
                                <Col md={3} className='text-right font-weight-bold pt-2'>
                                    {this.state.obj.componentCategory === 'Email' ? 'Subject:' : 'Date:'}
                                </Col>
                                <Col md={9}>
                                    <Input
                                        name="subject"
                                        type={this.state.obj.componentCategory === 'Email' ? "text" : "date"}
                                        disabled={this.state.obj.sentTimestamp != null || this.state.review}
                                        onChange={(e) => this.onChangeMarketingActivity(e)}
                                        value={this.state.obj.componentCategory === 'Email' ? subject : (subject != null && subject !== '' ? new Date(subject).toISOString().substring(0, 10) : '')}
                                    >
                                    </Input>
                                </Col>
                            </Row>
                            <Row className='mt-2'>
                                <Col md={3} className='text-right font-weight-bold pt-2'>
                                    Message:
                                </Col>
                                <Col>
                                    <Input
                                        name="message"
                                        type="textarea"
                                        disabled={this.state.obj.sentTimestamp != null || this.state.review}
                                        rows={15}
                                        onChange={(e) => this.onChangeMarketingActivity(e)}
                                        value={message}
                                    >
                                    </Input>
                                </Col>
                            </Row>
                            {attachments.length > 0 &&
                                // this.props.marketingActivities.length > 0 &&
                                // this.props.marketingActivities.filter(activity => activity.ActivityID === parseInt(this.state.editMarketingActivity.activityID)).length > 0 &&
                                // this.props.marketingActivities.filter(activity => activity.ActivityID === parseInt(this.state.editMarketingActivity.activityID))[0].Attachments != null &&
                                // this.props.marketingActivities.filter(activity => activity.ActivityID === parseInt(this.state.editMarketingActivity.activityID))[0].Attachments.length > 0 &&
                                <Row className='mt-2'>
                                    <Col md={3} className='pt-3 text-right font-weight-bold'>Attachments:</Col>
                                    <Col md={9} className='pl-5 py-2'>
                                        {attachments.map(attachment => (
                                            <Label key={attachment.FileID} className='my-2'>
                                                <Input
                                                    type="checkbox"
                                                    disabled={this.state.obj.sentTimestamp != null || this.state.review}
                                                    name="attachment"
                                                    id={"attachment_" + attachment.FileID + '_' + attachment.FileName}
                                                    onChange={(e) => this.onChangeMarketingActivity(e)}
                                                    checked={this.state.obj.content != null && this.state.obj.content.attachments != null ? this.state.obj.content.attachments.findIndex(a2 => a2.FileID === attachment.FileID) > -1 : false}
                                                >
                                                </Input>
                                                {attachment.FileName}
                                            </Label>
                                        ))}
                                    </Col>
                                </Row>}
                            <hr />
                        </>
                    }

                    {this.state.obj.componentIsActivity && this.state.obj.componentCategory === 'Call' &&
                        <>
                            <Row className='mt-2'>
                                <Col md={3} className='text-right font-weight-bold pt-2'>
                                    From:
                                </Col>
                                <Col md={9}>
                                    <Input
                                        type="select"
                                        name="auth0"
                                        disabled={this.state.obj.sentTimestamp != null || this.state.review}
                                        onChange={(e) => this.onChangeMarketingActivity(e)}
                                        value={this.state.obj.auth0}
                                    >
                                        <option value=''>Caller</option>
                                        {this.state.users.filter(user => user.Active).map(user => (
                                            <option key={user.Auth0} value={user.Auth0}>{user['Full Name']} &lt;{user['Email Address']}&gt;</option>
                                        ))}
                                    </Input>
                                </Col>
                            </Row>
                            <Row className='mt-2'>
                                <Col md={3} className='text-right font-weight-bold pt-2'>
                                    To:
                                </Col>
                                <Col md={9} className='pl-5'>
                                    {this.state.contacts != null && this.state.contacts.length > 0 && this.state.contacts.filter(contact => Object.keys(contact.children).findIndex(key => key.indexOf("Phone") > -1) > -1).map(contact => (
                                        <Label key={contact.linkedID} className='my-2'>
                                            <Input
                                                type="checkbox"
                                                disabled={this.state.obj.sentTimestamp != null || this.state.review}
                                                name="to"
                                                id={"to_" + contact.linkedID + '_' + contact.children['Contact Name']}
                                                onChange={(e) => this.onChangeMarketingActivity(e)}
                                                checked={this.state.obj.content != null && this.state.obj.content.to != null ? this.state.obj.content.to.findIndex(a2 => a2.linkedID === contact.linkedID) > -1 : false}
                                            >
                                            </Input>
                                            {contact.children['Contact Name']}
                                            {contact.children.Title != null && contact.children.Title !== '' ? ` (${contact.children.Title})` : ''}
                                            {contact.children['Phone Number (Mobile)'] != null && contact.children['Phone Number (Mobile)'] !== '' ? ` - ${contact.children['Phone Number (Mobile)']} (Mobile)` : ''}
                                            {contact.children['Phone Number (Office)'] != null && contact.children['Phone Number (Office)'] !== '' ? ` - ${contact.children['Phone Number (Office)']} (Office)` : ''}
                                            {contact.children['Phone Number (Personal)'] != null && contact.children['Phone Number (Personal)'] !== '' ? ` - ${contact.children['Phone Number (Personal)']} (Personal)` : ''}
                                            {contact.children['Phone Number (Home)'] != null && contact.children['Phone Number (Home)'] !== '' ? ` - ${contact.children['Phone Number (Home)']} (Home)` : ''}
                                        </Label>
                                    ))}
                                </Col>
                            </Row>
                            <Row className='mt-2'>
                                <Col md={3} className='text-right font-weight-bold pt-2'>
                                    Script:
                                </Col>
                                <Col>
                                    <Input
                                        name="message"
                                        type="textarea"
                                        disabled={this.state.obj.sentTimestamp != null || this.state.review}
                                        rows={15}
                                        onChange={(e) => this.onChangeMarketingActivity(e)}
                                        value={message}
                                    >
                                    </Input>
                                </Col>
                            </Row>
                            <hr />
                        </>
                    }

                    <Row className='mt-2'>
                        <Col md={3} className='text-right font-weight-bold pt-2'>
                            Notes:
                        </Col>
                        <Col>
                            <Input
                                name="notes"
                                type="textarea"
                                disabled={this.state.obj.sentTimestamp != null || this.state.review}
                                rows={7}
                                onChange={(e) => this.onChangeMarketingActivity(e)}
                                value={this.state.obj.notes}
                            >
                            </Input>
                        </Col>
                    </Row>
                </ModalBody>
                <ConfirmModal
                    executeAction={this.hideModal}
                    hideConfirmModal={() => this.setState({ confirmModalOpen: false })}
                    isOpen={this.state.confirmModalOpen}
                    body="You have unsaved changes."
                    action="Discard"
                    cancel="Continue Editing"
                    cancelColor="primary"
                />
                {!this.state.review && <ModalFooter>
                    <Row className='w-100'>
                        <Col className='text-left px-0'>{!this.state.contentNotesUpdated && this.state.retHTML}</Col>
                        <Col className='text-right px-0'>
                            <Button
                                color="secondary"
                                className='mr-2'
                                onClick={() => {
                                    if (this.state.originalState !== JSON.stringify(this.state.obj)) {
                                        this.setState({
                                            confirmModalOpen: true
                                        });
                                    } else {
                                        this.hideModal();
                                    }
                                }}
                            // onClick={this.hideModal}
                            >
                                Cancel
                            </Button>
                            <Button
                                color="secondary"
                                className='mr-4'
                                onClick={this.reset}
                                disabled={this.state.obj.content != null && (JSON.stringify(this.state.obj.content) === '{}' || this.state.obj.content === '') && this.state.obj.notes === ''}
                            >
                                Reset
                            </Button>
                            {this.state.obj.componentIsActivity &&
                                <a
                                    className='btn btn-primary mr-2'
                                    href={`mailto:${encodeURIComponent(this.state.obj.content != null && this.state.obj.content.to != null ? this.state.obj.content.to.map(address => {
                                        return `${address.name} <${address.email}>`
                                    }).join(', ') : '')}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`}
                                    onClick={this.handleEmailClick}
                                >
                                    Create Email
                                </a>}
                            <Button
                                onClick={this.handleContentNotes}
                                className='mr-2'
                                color="success"
                            >
                                Save
                            </Button>
                            {(this.state.obj.componentIsActivity || this.state.obj.componentIsDelay) && <Button
                                onClick={() => {
                                    this.handleContentNotes();
                                    this.props.markCompanyComponentComplete(this.state.obj.campaignCompanyID, this.state.obj.componentCompanyID, this.state.obj.componentID, this.state.obj.campaignID, this.state.obj.secID, this.state.obj.componentIsActivity ? this.state.obj.content : null, this.state.obj.auth0, this.state.obj.linkedCampaignCompanyIDs)
                                }}
                                className='mr-2'
                                color="success"
                            >
                                Save (+Done)
                            </Button>}
                        </Col>
                    </Row>
                </ModalFooter>}
            </Modal>
        )
    }
};

export default Marketing2ActionsModal;
